import { useState } from 'react';
import { CaretDown } from '@phosphor-icons/react';
import clsx from 'clsx';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  Card,
  CardHeader,
  Checkbox,
  Typography,
  Button,
  InputErrorMessage,
} from '@la/ds-ui-components';
import { ESignature } from 'components/ESignature/ESignature';
import { useRegistration } from 'lib/context/RegistrationContext/RegistrationContext';
import styles from './RegistrationWaiversCard.module.scss';

export const RegistrationWaiversCard = () => {
  const { ngESignature } = useFlags();

  const { eSignature, waivers, signedWaivers, dispatch, waiversTouched } =
    useRegistration();
  const [openedWaivers, setOpenedWaivers] = useState<string[]>(() => {
    return !waivers ? [] : waivers.map((waiver) => waiver.waiverId.toString());
  });

  const hasESignatureError = waiversTouched && ngESignature && !eSignature;

  if (!waivers) {
    return null;
  }

  const handleToggleAccordion = (id: string) => {
    if (openedWaivers.includes(id)) {
      setOpenedWaivers((previous) =>
        previous.filter((waiverId) => waiverId !== id)
      );
    } else {
      setOpenedWaivers((previous) => previous.concat(id));
    }
  };

  const handleCheckChange = (id: string, index: number) => {
    dispatch({ type: 'SET_SIGNED_WAIVERS', payload: { id } });
    if (openedWaivers.includes(id) && !signedWaivers[id]) {
      setOpenedWaivers(openedWaivers.filter((waiverId) => waiverId !== id));
      if (index < waivers.length - 1) {
        const nextWaiver = waivers[index + 1];
        if (!signedWaivers[nextWaiver.waiverId.toString()]) {
          setOpenedWaivers((previous) =>
            previous.concat(nextWaiver.waiverId.toString())
          );
          document.getElementById(`waiver_${index + 1}`)?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          });
        }
      }
    }
  };

  const handleESignatureChange = (signature?: string) => {
    dispatch({ type: 'SET_E_SIGNATURE', payload: signature });
  };

  return (
    <div className={styles.waiversContainer}>
      {waivers.map((waiver, i) => {
        const id = waiver.waiverId.toString();
        const isOpened = openedWaivers.includes(id);
        const hasWaiversError =
          waiversTouched && !signedWaivers[waiver.waiverId];
        return (
          <div key={id} id={`waiver_${i + 1}`}>
            <Card>
              <CardHeader>{waiver.name}</CardHeader>
              <div className={styles.cardBody}>
                <div className={styles.accordionTriggerContainer}>
                  <div className={styles.checkboxWrapper}>
                    <div className={styles.checkboxContainer}>
                      <Checkbox
                        id={waiver.name}
                        ariaLabel={waiver.name}
                        size="large"
                        checked={signedWaivers[id]}
                        onCheckedChange={() => handleCheckChange(id, i)}
                        hasError={hasWaiversError}
                      />
                      <label htmlFor={waiver.name}>
                        I have read and agree to the <b>{waiver.name}</b> waiver
                        below
                      </label>
                    </div>
                    {hasWaiversError ? (
                      <InputErrorMessage>
                        Please agree to the required document.
                      </InputErrorMessage>
                    ) : null}
                  </div>
                  <Button
                    onClick={() => handleToggleAccordion(id)}
                    variant="outline"
                    rightIcon={
                      <div
                        className={clsx({
                          [styles.AccordionCaretContainer]: true,
                          [styles.opened]: isOpened,
                        })}
                      >
                        <CaretDown
                          fill="var(--secondary-600)"
                          size={12}
                          weight="fill"
                        />
                      </div>
                    }
                  >
                    {isOpened ? 'Hide document' : 'Show document'}
                  </Button>
                </div>
              </div>
              {isOpened && (
                <div className={styles.accordionContentContainer}>
                  <div className={styles.accordionContent}>
                    <Typography variant="ui" size="large">
                      <span
                        dangerouslySetInnerHTML={{ __html: waiver.content }}
                      />
                    </Typography>
                  </div>
                </div>
              )}
            </Card>
          </div>
        );
      })}
      {ngESignature ? (
        <ESignature
          eSignature={eSignature}
          errorMessage="Please enter your full name."
          hasError={hasESignatureError}
          onChange={handleESignatureChange}
        />
      ) : null}
    </div>
  );
};
